/**
 * Lens prescriptions: spectacle and contact lens prescriptions, which lens dispensing jobs are made
 * from. Mirrors /api/lens-prescriptions on the REST API.
 *
 * Every call acts in Session.practice, which must have the lensDispensingFeature. Writing, issuing
 * and deleting need a prescribing role; reading does not.
 */
app.service("LensPrescriptionAPI", [
	"$http",
	"ENV",
	"Session",
	function ($http, ENV, Session) {
		const baseUri = ENV.API + "/api/lens-prescriptions";

		const getHeaders = function () {
			return {
				Authorization: "Bearer " + Session.token,
				"X-PRACTICE-ID": `${Session.practice.id}`,
			};
		};

		var service = {
			/** A patient's prescriptions, drafts included, newest first. */
			getByPatient: function (patientId) {
				return $http({
					method: "GET",
					url: `${baseUri}/patients/${encodeURIComponent(patientId)}`,
					headers: getHeaders(),
				});
			},

			/** One prescription. A 404 when the practice has no such prescription. */
			get: function (prescriptionId) {
				return $http({
					method: "GET",
					url: `${baseUri}/${encodeURIComponent(prescriptionId)}`,
					headers: getHeaders(),
				});
			},

			/**
			 * Creates a draft.
			 * @param {Object} prescription - { patient_id, pathway, spectacle_rx, advice, contact_lens_rx, fitting }
			 */
			create: function (prescription) {
				return $http({
					method: "POST",
					url: baseUri,
					headers: getHeaders(),
					data: prescription,
				});
			},

			/** Replaces a draft's content. Refused once it is issued. */
			update: function (prescriptionId, prescription) {
				return $http({
					method: "PUT",
					url: `${baseUri}/${encodeURIComponent(prescriptionId)}`,
					headers: getHeaders(),
					data: prescription,
				});
			},

			/**
			 * Issues a complete draft, which locks it.
			 * @param {string} [sentTo] - Where the patient's copy went, e.g. "email, printed".
			 */
			issue: function (prescriptionId, sentTo) {
				return $http({
					method: "POST",
					url: `${baseUri}/${encodeURIComponent(prescriptionId)}/issue`,
					headers: getHeaders(),
					data: { sent_to: sentTo || null },
				});
			},

			/**
			 * Withdraws an issued prescription found to be wrong. It is kept, with the reason, and can no
			 * longer be dispensed.
			 */
			withdraw: function (prescriptionId, reason) {
				return $http({
					method: "POST",
					url: `${baseUri}/${encodeURIComponent(prescriptionId)}/withdraw`,
					headers: getHeaders(),
					data: { reason: reason },
				});
			},

			/**
			 * The patient's copy of an issued (or withdrawn) prescription, made by the server as it is now -
			 * marked expired or withdrawn, and signed as fits whoever asks for it. Resolves to the PDF's bytes.
			 */
			getPatientCopy: function (prescriptionId) {
				return $http({
					method: "GET",
					url: `${baseUri}/${encodeURIComponent(prescriptionId)}/patient-copy`,
					headers: getHeaders(),
					responseType: "arraybuffer",
				});
			},

			/** Deletes a draft. Refused once it is issued. */
			remove: function (prescriptionId) {
				return $http({
					method: "DELETE",
					url: `${baseUri}/${encodeURIComponent(prescriptionId)}`,
					headers: getHeaders(),
				});
			},
		};

		return service;
	},
]);
